import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Brain, Download, Pencil, Plus, Trash2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clearMemory, createMemory, deleteMemory, exportMemory, getMemory, updateMemory } from '../services';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

type MemorySubject = {
  id: string;
  kind?: string;
  display_name?: string;
  relation?: string | null;
  chart_version?: number;
};

type MemoryItem = {
  id: string;
  subjectId?: string;
  text?: string;
  itemType?: string;
  category?: string;
  status?: string;
  sensitivity?: string;
  metadata?: Record<string, any>;
  updatedAt?: string;
};

type MemoryBundle = {
  state?: {
    profile?: {
      display_name?: string;
    };
    subjects?: MemorySubject[];
    memoryVersion?: number;
  };
  itemsBySubject?: Record<string, MemoryItem[]>;
  taxonomy?: {
    item_types?: string[];
    categories?: string[];
  };
  health?: {
    status?: string;
    title?: string;
    description?: string;
    last_failure_at?: string | null;
    degraded_until?: string | null;
    manual_actions_still_work?: boolean;
  };
};

/**
 * English fallbacks only.
 *
 * These used to be the labels themselves, which is why a translated page still
 * showed "Fact"; categories and statuses were worse — rendered as the raw
 * backend enum (`astro_interest`, `pending_confirmation`). The real labels now
 * come from `settings.memory_enum.*`, ported from the mobile locales so both
 * platforms name the same concept identically. This map survives as the
 * i18next `defaultValue` for an enum the backend adds before the locales
 * catch up.
 */
const ITEM_TYPE_LABELS: Record<string, string> = {
  fact: 'Fact',
  goal: 'Goal',
  constraint: 'Constraint',
  preference: 'Preference',
  interest: 'Interest',
  practice: 'Practice',
  session_summary: 'Session summary',
};

const STATUS_OPTIONS = ['active', 'in_progress', 'pending_confirmation', 'archived'];
const SENSITIVITY_OPTIONS = ['normal', 'sensitive'];
const PAGE_SIZE = 25;

const classifyQuickAdd = (text: string) => {
  const lower = text.trim().toLowerCase();
  if (!lower) return { item_type: 'fact', category: 'other' };
  if (lower.includes('prefer') || lower.includes('concise') || lower.includes('short answer')) {
    return { item_type: 'preference', category: 'style' };
  }
  if (lower.includes('goal') || lower.includes('i want') || lower.includes('i need') || lower.includes('before ')) {
    if (lower.includes('career') || lower.includes('job') || lower.includes('business')) {
      return { item_type: 'goal', category: 'career' };
    }
    return { item_type: 'goal', category: 'other' };
  }
  if (lower.includes('avoid') || lower.includes("don't") || lower.includes('do not') || lower.includes('vegetarian')) {
    return { item_type: 'constraint', category: lower.includes('vegetarian') ? 'dietary' : 'other' };
  }
  if (
    lower.includes('marriage') ||
    lower.includes('compatibility') ||
    lower.includes('career') ||
    lower.includes('jyotish') ||
    lower.includes('vedanta') ||
    lower.includes('ramayana')
  ) {
    return {
      item_type: 'interest',
      category: lower.includes('vedanta') || lower.includes('ramayana') ? 'scriptural_interest' : 'astro_interest',
    };
  }
  return { item_type: 'fact', category: 'other' };
};

const MemorySettingsPanel: React.FC = () => {
  const { t } = useTranslation('settings');
  const { toast } = useToast();

  /**
   * Localized name for a profile's relation.
   *
   * Relations are a backend enum (`SUBJECT_RELATIONS` in `memory_taxonomy.py`
   * plus the values the mobile chart form sends), and both profile lists below
   * used to interpolate them raw — so a Hindi or Japanese user read
   * "प्रिया · sister": an English database value inside a translated label.
   *
   * The raw value is the i18next `defaultValue`, so an enum added to the
   * backend later still renders as itself rather than as a missing-key blank.
   */
  const relationLabel = (relation?: string | null) => {
    const key = (relation ?? '').trim();
    if (!key) return '';
    return t(`settings.relation.${key}`, { defaultValue: key });
  };

  /**
   * Localized names for the memory taxonomy enums.
   *
   * Every one of these was reaching the user as a backend identifier: item
   * types via a hardcoded English map, and categories / statuses /
   * sensitivities entirely unstyled — `astro_interest`, `pending_confirmation`.
   * The `defaultValue` keeps an unrecognised enum readable rather than blank.
   */
  const enumLabel = (prefix: string, value?: string | null, fallback?: string) => {
    const key = (value ?? '').trim();
    if (!key) return '';
    return t(`settings.memory_enum.${prefix}_${key}`, { defaultValue: fallback ?? key });
  };
  const itemTypeLabel = (value?: string | null) =>
    enumLabel('type', value, ITEM_TYPE_LABELS[(value ?? '').trim()]);
  const categoryLabel = (value?: string | null) => enumLabel('cat', value);
  const statusLabel = (value?: string | null) => enumLabel('status', value);
  const sensitivityLabel = (value?: string | null) => enumLabel('sensitivity', value);
  const [memoryBundle, setMemoryBundle] = useState<MemoryBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubjectId, setSelectedSubjectId] = useState('self');
  const [quickAddText, setQuickAddText] = useState('');
  const [structuredOpen, setStructuredOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearScope, setClearScope] = useState<'subject' | 'all' | null>(null);
  const [clearing, setClearing] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState({
    text: '',
    item_type: 'fact',
    category: 'other',
    status: 'active',
    sensitivity: 'normal',
    target_date: '',
    subject_id: 'self',
  });
  const [structuredDraft, setStructuredDraft] = useState({
    text: '',
    item_type: 'fact',
    category: 'other',
    status: 'active',
    sensitivity: 'normal',
    target_date: '',
  });
  const [currentPage, setCurrentPage] = useState(0);

  const fetchMemoryBundle = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getMemory();
      setMemoryBundle(result?.memory || null);
    } catch (error) {
      console.error('Failed to fetch memory', error);
      toast({
        title: t('settings.memory_toast_load_failed'),
        description: t('settings.memory_toast_retry_moment'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    void fetchMemoryBundle();
  }, [fetchMemoryBundle]);

  useEffect(() => {
    const onFocus = () => {
      void fetchMemoryBundle();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchMemoryBundle]);

  const taxonomy = memoryBundle?.taxonomy || {};
  const itemTypes = taxonomy.item_types || ['fact', 'goal', 'constraint', 'preference', 'interest', 'practice'];
  const categories = taxonomy.categories || ['other'];
  const memoryHealth = memoryBundle?.health;

  const subjects = useMemo(() => {
    const serverSubjects = Array.isArray(memoryBundle?.state?.subjects) ? [...(memoryBundle?.state?.subjects || [])] : [];
    const hasSelf = serverSubjects.some((subject) => subject.id === 'self');
    if (!hasSelf) {
      serverSubjects.unshift({
        id: 'self',
        kind: 'self',
        display_name: memoryBundle?.state?.profile?.display_name || t('settings.memory_subject_you'),
      });
    }
    return serverSubjects;
  }, [memoryBundle, t]);

  useEffect(() => {
    if (!subjects.some((subject) => subject.id === selectedSubjectId)) {
      setSelectedSubjectId(subjects[0]?.id || 'self');
    }
  }, [selectedSubjectId, subjects]);

  const selectedItems = useMemo(() => {
    const grouped = memoryBundle?.itemsBySubject || {};
    const items = Array.isArray(grouped[selectedSubjectId]) ? grouped[selectedSubjectId] : [];
    return [...items].sort((left, right) => String(right.updatedAt || '').localeCompare(String(left.updatedAt || '')));
  }, [memoryBundle, selectedSubjectId]);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedSubjectId]);

  const totalPages = Math.max(1, Math.ceil(selectedItems.length / PAGE_SIZE));
  const pageStart = currentPage * PAGE_SIZE;
  const pageItems = selectedItems.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [currentPage, totalPages]);

  const totalItemCount = useMemo(() => {
    const grouped = memoryBundle?.itemsBySubject || {};
    return Object.values(grouped).reduce((total, items) => total + (Array.isArray(items) ? items.length : 0), 0);
  }, [memoryBundle]);

  const totalSubjectCount = useMemo(() => {
    const grouped = memoryBundle?.itemsBySubject || {};
    return Object.values(grouped).filter((items) => Array.isArray(items) && items.length > 0).length;
  }, [memoryBundle]);

  const selectedSubject = useMemo(
    () => subjects.find((subject) => subject.id === selectedSubjectId),
    [selectedSubjectId, subjects],
  );

  const selectedSubjectLabel = useMemo(() => {
    if (!selectedSubject) return t('settings.memory_subject_default');
    if (selectedSubject.id === 'self') {
      return selectedSubject.display_name || t('settings.memory_subject_you');
    }
    return selectedSubject.display_name || t('settings.memory_subject_default');
  }, [selectedSubject, t]);

  const quickSuggestion = useMemo(() => classifyQuickAdd(quickAddText), [quickAddText]);

  const resetStructuredDraft = () => {
    setStructuredDraft({
      text: '',
      item_type: 'fact',
      category: 'other',
      status: 'active',
      sensitivity: 'normal',
      target_date: '',
    });
  };

  const handleQuickAdd = async () => {
    const text = quickAddText.trim();
    if (!text) return;
    try {
      setSaving(true);
      await createMemory({
        text,
        item_type: quickSuggestion.item_type,
        category: quickSuggestion.category,
        subject_id: selectedSubjectId,
        metadata: {},
      });
      setQuickAddText('');
      await fetchMemoryBundle();
      const firstToastKey = 'sarvalok_memory_first_toast_shown_v1';
      let alreadyShown = true;
      try {
        alreadyShown = localStorage.getItem(firstToastKey) === '1';
      } catch {
        alreadyShown = true;
      }
      if (!alreadyShown) {
        try {
          localStorage.setItem(firstToastKey, '1');
        } catch {}
        toast({
          title: t('settings.memory_toast_first_title'),
          description: t('settings.memory_toast_first_description'),
        });
      } else {
        toast({ title: t('settings.memory_toast_saved') });
      }
    } catch (error) {
      console.error('Failed to quick-add memory', error);
      toast({
        title: t('settings.memory_toast_save_failed'),
        description: t('settings.memory_toast_retry'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStructuredAdd = async () => {
    const text = structuredDraft.text.trim();
    if (!text) return;
    try {
      setSaving(true);
      await createMemory({
        text,
        item_type: structuredDraft.item_type,
        category: structuredDraft.category,
        subject_id: selectedSubjectId,
        status: structuredDraft.status,
        sensitivity: structuredDraft.sensitivity,
        metadata: structuredDraft.target_date ? { target_date: structuredDraft.target_date } : {},
      });
      resetStructuredDraft();
      setStructuredOpen(false);
      await fetchMemoryBundle();
      const firstToastKey = 'sarvalok_memory_first_toast_shown_v1';
      let alreadyShown = true;
      try {
        alreadyShown = localStorage.getItem(firstToastKey) === '1';
      } catch {
        alreadyShown = true;
      }
      if (!alreadyShown) {
        try {
          localStorage.setItem(firstToastKey, '1');
        } catch {}
        toast({
          title: t('settings.memory_toast_first_title'),
          description: t('settings.memory_toast_first_description'),
        });
      } else {
        toast({ title: t('settings.memory_toast_added') });
      }
    } catch (error) {
      console.error('Failed to add structured memory', error);
      toast({
        title: t('settings.memory_toast_add_failed'),
        description: t('settings.memory_toast_retry'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (item: MemoryItem) => {
    setEditingItemId(item.id);
    setEditDraft({
      text: item.text || '',
      item_type: item.itemType || 'fact',
      category: item.category || 'other',
      status: item.status || 'active',
      sensitivity: item.sensitivity || 'normal',
      target_date: typeof item.metadata?.target_date === 'string' ? item.metadata.target_date : '',
      subject_id: item.subjectId || selectedSubjectId,
    });
  };

  const handleSaveEdit = async (itemId: string) => {
    try {
      setSaving(true);
      await updateMemory(itemId, {
        text: editDraft.text.trim(),
        item_type: editDraft.item_type,
        category: editDraft.category,
        subject_id: editDraft.subject_id || selectedSubjectId,
        status: editDraft.status,
        sensitivity: editDraft.sensitivity,
        metadata: editDraft.target_date ? { target_date: editDraft.target_date } : {},
      });
      setEditingItemId(null);
      await fetchMemoryBundle();
      toast({ title: t('settings.memory_toast_updated') });
    } catch (error) {
      console.error('Failed to update memory', error);
      toast({
        title: t('settings.memory_toast_update_failed'),
        description: t('settings.memory_toast_retry'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId: string) => {
    try {
      setDeleting(true);
      await deleteMemory(itemId);
      await fetchMemoryBundle();
      setDeleteItemId(null);
      toast({ title: t('settings.memory_toast_deleted') });
    } catch (error) {
      console.error('Failed to delete memory', error);
      toast({
        title: t('settings.memory_toast_delete_failed'),
        description: t('settings.memory_toast_retry'),
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const confirmClear = async () => {
    if (!clearScope) return;
    try {
      setClearing(true);
      await clearMemory({ subject_id: clearScope === 'subject' ? selectedSubjectId : null });
      await fetchMemoryBundle();
      toast({
        title: clearScope === 'all'
          ? t('settings.memory_toast_cleared_all')
          : t('settings.memory_toast_cleared_subject'),
      });
      setClearScope(null);
    } catch (error) {
      console.error('Failed to clear memory', error);
      toast({
        title: t('settings.memory_toast_clear_failed'),
        description: t('settings.memory_toast_retry'),
        variant: 'destructive',
      });
    } finally {
      setClearing(false);
    }
  };

  const handleExport = async () => {
    try {
      const result = await exportMemory();
      const blob = new Blob([JSON.stringify(result?.memory || {}, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      anchor.href = url;
      anchor.download = `sarvalok-memory-export-${today}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export memory', error);
      toast({
        title: t('settings.memory_toast_export_failed'),
        description: t('settings.memory_toast_retry'),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <section className="sl-memory-settings-panel rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-5 text-black shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-amber-100 p-2 text-amber-700">
            <Brain className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{t('settings.memory')}</h2>
            <p className="mt-1 text-sm text-slate-600">
              {t('settings.memory_description')}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => void fetchMemoryBundle()}
            className="sl-memory-neutral-button inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:border-slate-400"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('settings.refresh')}
          </button>
          <button
            onClick={() => void handleExport()}
            className="sl-memory-neutral-button inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:border-slate-400"
          >
            <Download className="h-4 w-4" />
            {t('settings.export')}
          </button>
        </div>
      </div>

      {memoryHealth?.status === 'degraded' && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">{memoryHealth.title || t('settings.memory_health_fallback_title')}</p>
          <p className="mt-1">{memoryHealth.description || t('settings.memory_health_fallback_description')}</p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {subjects.map((subject) => {
          const isActive = subject.id === selectedSubjectId;
          const label = subject.id === 'self'
            ? (subject.display_name || t('settings.memory_subject_you'))
            : `${subject.display_name || t('settings.memory_subject_fallback')}${subject.relation ? ` · ${relationLabel(subject.relation)}` : ''}`;
          return (
            <button
              key={subject.id}
              onClick={() => setSelectedSubjectId(subject.id)}
              className={`sl-memory-subject-tab rounded-full border px-3 py-2 text-sm transition ${
                isActive
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1.15fr,0.85fr]">
        <div className="sl-memory-card rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{t('settings.memory_quick_add_title')}</h3>
              <p className="mt-1 text-sm text-slate-600">{t('settings.memory_quick_add_description')}</p>
            </div>
            <button
              onClick={() => setStructuredOpen((current) => !current)}
              className="sl-memory-neutral-button rounded-lg border border-slate-300 px-3 py-2 text-sm hover:border-slate-400"
            >
              {structuredOpen ? t('settings.memory_structured_add_toggle_hide') : t('settings.memory_structured_add_toggle_show')}
            </button>
          </div>

          <div className="mt-4 flex flex-col gap-3">
            <textarea
              value={quickAddText}
              onChange={(e) => setQuickAddText(e.target.value.slice(0, 280))}
              placeholder={t('settings.memory_quick_add_placeholder')}
              className="sl-memory-input min-h-[96px] rounded-xl border border-slate-300 px-3 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
              <span>
                {t('settings.memory_suggested_save')}{' '}
                <strong>{itemTypeLabel(quickSuggestion.item_type)}</strong> · {categoryLabel(quickSuggestion.category)}
              </span>
              <span>{quickAddText.length}/280</span>
            </div>
            <button
              onClick={() => void handleQuickAdd()}
              disabled={saving || !quickAddText.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              {t('settings.memory_quick_add_save_button')}
            </button>
          </div>

          {structuredOpen && (
            <div className="sl-memory-structured-box mt-5 rounded-2xl border border-dashed border-amber-300 bg-amber-50/60 p-4">
              <h4 className="text-sm font-semibold text-slate-800">{t('settings.memory_structured_add_title')}</h4>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <select
                  value={structuredDraft.item_type}
                  onChange={(e) => setStructuredDraft((prev) => ({ ...prev, item_type: e.target.value }))}
                  className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  {itemTypes.map((itemType) => (
                    <option key={itemType} value={itemType}>{itemTypeLabel(itemType)}</option>
                  ))}
                </select>
                <select
                  value={structuredDraft.category}
                  onChange={(e) => setStructuredDraft((prev) => ({ ...prev, category: e.target.value }))}
                  className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>{categoryLabel(category)}</option>
                  ))}
                </select>
                <select
                  value={structuredDraft.status}
                  onChange={(e) => setStructuredDraft((prev) => ({ ...prev, status: e.target.value }))}
                  className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>{statusLabel(status)}</option>
                  ))}
                </select>
                <select
                  value={structuredDraft.sensitivity}
                  onChange={(e) => setStructuredDraft((prev) => ({ ...prev, sensitivity: e.target.value }))}
                  className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  {SENSITIVITY_OPTIONS.map((sensitivity) => (
                    <option key={sensitivity} value={sensitivity}>{sensitivityLabel(sensitivity)}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={structuredDraft.target_date}
                  onChange={(e) => setStructuredDraft((prev) => ({ ...prev, target_date: e.target.value }))}
                  className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm md:col-span-2"
                />
                <textarea
                  value={structuredDraft.text}
                  onChange={(e) => setStructuredDraft((prev) => ({ ...prev, text: e.target.value.slice(0, 280) }))}
                  placeholder={t('settings.memory_structured_add_placeholder')}
                  className="sl-memory-input min-h-[110px] rounded-xl border border-slate-300 px-3 py-3 text-sm md:col-span-2"
                />
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => void handleStructuredAdd()}
                  disabled={saving || !structuredDraft.text.trim()}
                  className="rounded-xl bg-amber-600 px-4 py-3 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t('settings.memory_structured_add_button')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="sl-memory-card rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{t('settings.memory_saved_items_title')}</h3>
              <p className="mt-1 text-sm text-slate-600">
                {loading ? t('settings.memory_saved_items_loading') : t('settings.memory_saved_items_count', { count: selectedItems.length })}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setClearScope('subject')}
                className="sl-memory-danger-button rounded-lg border border-slate-300 px-3 py-2 text-sm hover:border-red-400 hover:text-red-700"
              >
                {t('settings.memory_forget_subject_button')}
              </button>
              <button
                onClick={() => setClearScope('all')}
                className="sl-memory-danger-button rounded-lg border border-slate-300 px-3 py-2 text-sm hover:border-red-400 hover:text-red-700"
              >
                {t('settings.memory_forget_all_button')}
              </button>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {loading && (
              <div className="sl-memory-muted-card rounded-xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                {t('settings.memory_saved_items_loading')}
              </div>
            )}

            {!loading && selectedItems.length === 0 && (
              <div className="sl-memory-muted-card rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                {t('settings.memory_empty_subject')}
              </div>
            )}

            {!loading && pageItems.map((item) => {
              const isEditing = editingItemId === item.id;
              const targetDate = typeof item.metadata?.target_date === 'string' ? item.metadata.target_date : '';
              return (
                <div key={item.id} className="sl-memory-muted-card rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  {isEditing ? (
                    <div className="space-y-3">
                      <textarea
                        value={editDraft.text}
                        onChange={(e) => setEditDraft((prev) => ({ ...prev, text: e.target.value.slice(0, 280) }))}
                        className="sl-memory-input min-h-[96px] w-full rounded-xl border border-slate-300 px-3 py-3 text-sm"
                      />
                      <div className="grid gap-3 md:grid-cols-2">
                        <select
                          value={editDraft.subject_id}
                          onChange={(e) => setEditDraft((prev) => ({ ...prev, subject_id: e.target.value }))}
                          className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm md:col-span-2"
                        >
                          {subjects.map((subject) => {
                            const label = subject.id === 'self'
                              ? (subject.display_name || t('settings.memory_subject_you'))
                              : `${subject.display_name || t('settings.memory_subject_fallback')}${subject.relation ? ` · ${relationLabel(subject.relation)}` : ''}`;
                            return (
                              <option key={subject.id} value={subject.id}>{label}</option>
                            );
                          })}
                        </select>
                        <select
                          value={editDraft.item_type}
                          onChange={(e) => setEditDraft((prev) => ({ ...prev, item_type: e.target.value }))}
                          className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        >
                          {itemTypes.map((itemType) => (
                            <option key={itemType} value={itemType}>{itemTypeLabel(itemType)}</option>
                          ))}
                        </select>
                        <select
                          value={editDraft.category}
                          onChange={(e) => setEditDraft((prev) => ({ ...prev, category: e.target.value }))}
                          className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        >
                          {categories.map((category) => (
                            <option key={category} value={category}>{categoryLabel(category)}</option>
                          ))}
                        </select>
                        <select
                          value={editDraft.status}
                          onChange={(e) => setEditDraft((prev) => ({ ...prev, status: e.target.value }))}
                          className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        >
                          {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>{statusLabel(status)}</option>
                          ))}
                        </select>
                        <select
                          value={editDraft.sensitivity}
                          onChange={(e) => setEditDraft((prev) => ({ ...prev, sensitivity: e.target.value }))}
                          className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        >
                          {SENSITIVITY_OPTIONS.map((sensitivity) => (
                            <option key={sensitivity} value={sensitivity}>{sensitivityLabel(sensitivity)}</option>
                          ))}
                        </select>
                        <input
                          type="date"
                          value={editDraft.target_date}
                          onChange={(e) => setEditDraft((prev) => ({ ...prev, target_date: e.target.value }))}
                          className="sl-memory-input rounded-lg border border-slate-300 px-3 py-2 text-sm md:col-span-2"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingItemId(null)}
                          className="sl-memory-neutral-button rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        >
                          {t('settings.memory_edit_cancel')}
                        </button>
                        <button
                          onClick={() => void handleSaveEdit(item.id)}
                          className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white"
                        >
                          {t('settings.memory_edit_save_changes')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{item.text}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white">
                              {itemTypeLabel(item.itemType || 'fact')}
                            </span>
                            <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                              {categoryLabel(item.category || 'other')}
                            </span>
                            <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                              {statusLabel(item.status || 'active')}
                            </span>
                            <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                              {sensitivityLabel(item.sensitivity || 'normal')}
                            </span>
                            {targetDate && (
                              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-medium text-amber-800">
                                {t('settings.memory_item_target_label', { date: targetDate })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => startEditing(item)}
                            className="sl-memory-icon-button rounded-lg border border-slate-300 p-2 text-slate-600 hover:border-slate-500 hover:text-slate-900"
                            title={t('settings.memory_item_edit_title')}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteItemId(item.id)}
                            className="sl-memory-icon-button rounded-lg border border-slate-300 p-2 text-slate-600 hover:border-red-400 hover:text-red-700"
                            title={t('settings.memory_item_delete_title')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {!loading && totalPages > 1 && (
            <div className="sl-memory-pagination mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-3 text-sm">
              <button
                onClick={() => setCurrentPage((page) => Math.max(0, page - 1))}
                disabled={currentPage === 0}
                className="sl-memory-pagination-button rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                ← {t('settings.memory_pagination_previous', { defaultValue: 'Previous' })}
              </button>
              <span className="sl-memory-pagination-status text-slate-600">
                {t('settings.memory_pagination_status', {
                  defaultValue: 'Page {{current}} of {{total}} · {{count}} items',
                  current: currentPage + 1,
                  total: totalPages,
                  count: selectedItems.length,
                })}
              </span>
              <button
                onClick={() => setCurrentPage((page) => Math.min(totalPages - 1, page + 1))}
                disabled={currentPage >= totalPages - 1}
                className="sl-memory-pagination-button rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('settings.memory_pagination_next', { defaultValue: 'Next' })} →
              </button>
            </div>
          )}
        </div>
      </div>
    </section>

      <AlertDialog
        open={deleteItemId !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteItemId(null);
        }}
      >
        <AlertDialogContent className="sl-memory-confirm-content border-slate-200 bg-white text-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="sl-memory-confirm-title">
              {t('settings.memory_item_delete_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="sl-memory-confirm-description text-slate-600">
              {t('settings.memory_confirm_undo')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sl-memory-confirm-footer">
            <AlertDialogCancel
              className="sl-memory-confirm-cancel border-slate-300 text-slate-700 hover:bg-slate-100"
              disabled={deleting}
            >
              {t('settings.memory_confirm_cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteItemId) void handleDelete(deleteItemId);
              }}
              className="sl-memory-confirm-action bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
              disabled={deleting}
            >
              {t('settings.memory_item_delete_title')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clearScope !== null} onOpenChange={(open) => { if (!open) setClearScope(null); }}>
        <AlertDialogContent className="sl-memory-confirm-content border-slate-200 bg-white text-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="sl-memory-confirm-title">
              {clearScope === 'all'
                ? t('settings.memory_confirm_all_title')
                : t('settings.memory_confirm_subject_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="sl-memory-confirm-description text-slate-600">
              {clearScope === 'all'
                ? t('settings.memory_confirm_all_description')
                : t('settings.memory_confirm_subject_description', { subject: selectedSubjectLabel })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="sl-memory-confirm-detail-box space-y-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <p>
              {clearScope === 'all'
                ? t('settings.memory_confirm_all_items', { count: totalItemCount })
                : t('settings.memory_confirm_subject_items', { count: selectedItems.length })}
            </p>
            <p>
              {clearScope === 'all'
                ? t('settings.memory_confirm_all_subjects', { count: totalSubjectCount })
                : t('settings.memory_confirm_subject_scope', { subject: selectedSubjectLabel })}
            </p>
            <p className="sl-memory-confirm-undo font-medium text-red-700">{t('settings.memory_confirm_undo')}</p>
          </div>
          <AlertDialogFooter className="sl-memory-confirm-footer">
            <AlertDialogCancel className="sl-memory-confirm-cancel border-slate-300 text-slate-700 hover:bg-slate-100">
              {t('settings.memory_confirm_cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmClear()}
              className="sl-memory-confirm-action bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
              disabled={clearing}
            >
              {clearScope === 'all'
                ? t('settings.memory_confirm_action_all')
                : t('settings.memory_confirm_action_subject')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MemorySettingsPanel;
